/**
 * Purchase-quantity engine (Phase 6).
 *
 * Converts a CONSTRUCTION requirement (Phase 4's effective component
 * quantity, waste already applied) into PURCHASE units using the selected
 * product's coverage. One rule: ceil to whole purchase units, always up.
 * NO product-level waste factor — waste belongs to the Assembly component;
 * package-rounding excess is reported separately.
 */
export interface PurchaseQuantityResult {
  requiredX100: number;
  coveragePerUnitX100: number;
  /** required ÷ coverage, hundredths (display only). */
  rawUnitsX100: number;
  /** Whole purchase units, rounded UP. */
  purchaseUnits: number;
  /** purchaseUnits × coverage. */
  purchasedCoverageX100: number;
  /** purchasedCoverage − required (package-rounding excess, NOT waste). */
  roundingExcessX100: number;
}

export function computePurchaseQuantity(
  requiredX100: number,
  coveragePerUnitX100: number,
): PurchaseQuantityResult {
  if (coveragePerUnitX100 <= 0) {
    throw new Error("Coverage per purchase unit must be positive.");
  }
  if (requiredX100 <= 0) {
    return {
      requiredX100,
      coveragePerUnitX100,
      rawUnitsX100: 0,
      purchaseUnits: 0,
      purchasedCoverageX100: 0,
      roundingExcessX100: 0,
    };
  }
  const purchaseUnits = Math.ceil(requiredX100 / coveragePerUnitX100);
  const rawUnitsX100 = Math.round((requiredX100 * 100) / coveragePerUnitX100);
  const purchasedCoverageX100 = purchaseUnits * coveragePerUnitX100;
  return {
    requiredX100,
    coveragePerUnitX100,
    rawUnitsX100,
    purchaseUnits,
    purchasedCoverageX100,
    roundingExcessX100: purchasedCoverageX100 - requiredX100,
  };
}
